import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";

const getSizeValue = (value, fallback) =>
  typeof value === "number" ? `${value}px` : value ?? fallback;

const placeholderPulse = keyframes`
  0% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.6;
  }
  100% {
    opacity: 0.3;
  }
`;

export const FooterWrapper = styled.footer`
  background: ${({ theme }) => theme.green};
  padding: 4rem 0 2rem 0;
  color: white;
`;

export const Container = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

export const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 4fr;
  gap: 3rem;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
    margin-bottom: 2rem;
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    gap: 2rem;
  }
`;

export const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2rem;
  @media (max-width: 768px) {
    order: -1;
  }
`;

export const LogoLink = styled(Link)`
  width: 357px;
  height: 125px;
  display: flex;

  img {
    width: 357px;
    height: 125px;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    width: 361px;
    height: 126px;

    img {
      width: 361px;
      height: 126px;
    }
  }
`;

export const Logo = styled.img`
  width: 350px;
  height: 122px;
  margin-bottom: 1rem;
  cursor: pointer;
  object-fit: contain;

  @media (max-width: 768px) {
    width: 361px;
    height: 126px;
  }
`;

export const Description = styled.p`
  font-family: ${({ theme }) => theme.fonts.sofia};
  font-size: 14px;
  line-height: 1.6;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
`;

export const MenuSectionsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 3rem;

  @media (min-width: 769px) and (max-width: 1024px) {
    grid-template-columns: 1fr 1fr 1fr;
    gap: 2rem;
  }

  @media (max-width: 768px) {
    display: flex;
    flex-wrap: wrap;
    gap: 2rem;
  }
`;

export const MenuSection = styled.div`
  @media (max-width: 768px) {
    flex: 1;
    min-width: calc(50% - 1rem);
  }
`;

export const SectionTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.ivy};
  font-size: 18px;
  font-weight: 400;
  margin-bottom: 1.5rem;
  color: white;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 1rem;
  }
`;

export const MenuList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const MenuListItem = styled.li`
  margin-bottom: 0.75rem;
`;

export const MenuLink = styled.a`
  font-family: ${({ theme }) => theme.fonts.sofia};
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: white;
    text-decoration: underline;
  }
`;

export const MenuPlaceholder = styled.li`
  display: block;
  height: 21px;

  width: 80%;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.2);
  animation: ${placeholderPulse} 1.6s ease-in-out infinite;
`;

export const Divider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.2);
  margin: 2rem 0;
`;

export const BottomSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 3rem;
  }
`;

export const PartnerLogos = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 3rem;
  }
`;

export const PartnerLogo = styled.img`
  height: ${({ $height }) => getSizeValue($height, "68px")};
  width: ${({ $width }) => getSizeValue($width, "68px")};
  object-fit: contain;

  @media (max-width: 768px) {
    height: 88px;
    width: 88px;
  }
`;

export const ContactSocialSection = styled.div`
  display: flex;
  gap: 4rem;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 3rem;
    width: 100%;
  }
`;

export const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const SocialInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const InfoTitle = styled.h4`
  font-family: ${({ theme }) => theme.fonts.ivy};
  font-size: 16px;
  margin: 0 0 0.5rem 0;
  color: white;
`;

export const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-family: ${({ theme }) => theme.fonts.sofia};
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
`;

export const SocialItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-family: ${({ theme }) => theme.fonts.sofia};
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
`;

export const ContactLink = styled.a`
  color: rgba(255, 255, 255, 0.8);
  text-decoration: ${(props) => (props.$underline ? "underline" : "none")};
  transition: color 0.3s ease;

  &:hover {
    color: white;
  }
`;

export const ContactText = styled.span`
  color: rgba(255, 255, 255, 0.8);
`;

export const AppStoreSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  gap: 1rem;

  @media (max-width: 768px) {
    justify-content: center;
    flex-direction: row;
  }
`;

export const AppButton = styled.button`
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

export const AppButtonImage = styled.img`
  height: 40px;
  width: 120px;
  object-fit: contain;
`;

export const FinalBottom = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);

  @media (max-width: 768px) {
    text-align: center;
  }
`;

export const CopyrightSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

export const CopyrightText = styled.p`
  font-family: ${({ theme }) => theme.fonts.sofia};
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;

  @media (max-width: 768px) {
    order: 1;
  }
`;

export const AgencyLogo = styled.img`
  height: 24px;
  width: 120px;
  object-fit: contain;
  filter: brightness(0) invert(1);
  opacity: 0.9;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 1;
  }

  @media (max-width: 768px) {
    order: 2;
    height: 20px;
    width: 100px;
  }
`;

export const PrivacyLink = styled.a`
  font-family: ${({ theme }) => theme.fonts.sofia};
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: white;
    text-decoration: underline;
  }

  @media (max-width: 768px) {
    order: 3;
  }
`;
